import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import Button from '../Button.jsx';
import ModalLayout from '../ModalLayout.jsx';
import { useDrivePickerTree } from './useDrivePickerTree.js';
import { buildConfirmItems } from './drivePickerUtils.js';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';

const FILE_ICON_CLASS = {
  drive: 'text-brand-600 dark:text-brand-400',
  folder: 'text-brand-600 dark:text-brand-400',
  pdf: 'text-red-600 dark:text-red-400',
  docx: 'text-blue-600 dark:text-blue-400',
  doc: 'text-blue-600 dark:text-blue-400',
  xlsx: 'text-lime-600 dark:text-lime-400',
  xls: 'text-lime-600 dark:text-lime-400',
  pptx: 'text-orange-600 dark:text-orange-400',
  ppt: 'text-orange-600 dark:text-orange-400',
  png: 'text-violet-600 dark:text-violet-400',
  jpg: 'text-violet-600 dark:text-violet-400',
  jpeg: 'text-violet-600 dark:text-violet-400',
  default: 'text-content-secondary',
};

const getFileIconClass = (type) =>
  FILE_ICON_CLASS[type] ?? FILE_ICON_CLASS.default;

const FileItem = memo(
  ({ file, isExpanded, isSelected, isLoading, onToggle }) => {
    const iconClass = getFileIconClass(file.type);
    const isExpandable = file.type === 'folder' || file.type === 'drive';
    const showDocIcon =
      file.type !== 'folder' &&
      file.type !== 'drive' &&
      (file.type === 'png' ||
        file.type === 'jpg' ||
        file.type === 'jpeg');

    const handleClick = useCallback(() => {
      onToggle(file);
    }, [onToggle, file]);

    return (
      <button
        type="button"
        onClick={handleClick}
        className={[
          'flex min-h-[44px] w-full cursor-pointer items-center justify-between rounded-md border border-transparent px-3 py-2.5 text-left transition-colors',
          isSelected
            ? 'border-brand-500 bg-brand-500/10 dark:border-brand-400 dark:bg-brand-400/10'
            : 'hover:bg-surface-muted dark:hover:bg-surface-muted/80',
        ].join(' ')}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2.5">
          {file.type === 'folder' || file.type === 'drive' ? (
            <Folder className={`h-[18px] w-[18px] shrink-0 ${iconClass}`} />
          ) : showDocIcon ? (
            <ImageIcon className={`h-[18px] w-[18px] shrink-0 ${iconClass}`} />
          ) : (
            <FileText className={`h-[18px] w-[18px] shrink-0 ${iconClass}`} />
          )}
          <span
            className="truncate text-sm text-content"
            title={file.name}
          >
            {file.name}
          </span>
        </span>
        <span className="ml-2 flex shrink-0 items-center justify-end">
          {isExpandable &&
            (isLoading ? (
              <Loader2
                className="h-[18px] w-[18px] animate-spin text-content-muted"
                aria-hidden
              />
            ) : isExpanded ? (
              <ChevronDown
                className="h-[18px] w-[18px] text-content-muted"
                aria-hidden
              />
            ) : (
              <ChevronRight
                className="h-[18px] w-[18px] text-content-muted"
                aria-hidden
              />
            ))}
        </span>
      </button>
    );
  }
);

FileItem.displayName = 'FileItem';

const TreeLoadingRow = memo(({ message }) => (
  <div className="flex items-center gap-2 px-3 py-2 text-sm text-content-muted">
    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
    <span>{message}</span>
  </div>
));
TreeLoadingRow.displayName = 'TreeLoadingRow';

/** Clase de scroll del cuerpo del árbol (plugin `@aimsos/ui/tailwind-scrollbar-aimsos-plugin`). */
const DEFAULT_BODY_SCROLLBAR_CLASS = 'scrollbar-aimsos-auto';

/**
 * Selector de documentos en árbol de drives / carpetas, montado sobre **`ModalLayout`** (shell).
 *
 * ### Tamaño por defecto
 * Panel **`max-w-[600px] w-full`** y **`max-h-[600px]`**; si el árbol supera el alto, el cuerpo usa scroll con **`scrollbar-aimsos-auto`**. Podés sustituir con `maxWidthClassName` y `heightClassName`.
 *
 * ### Cómo cablear datos en una app externa
 * No hay una sola prop “API”: el componente es **presentacional** y vos inyectás datos y red.
 *
 * 1. **`drives`** — Arreglo que vos obtenés (REST, SDK, etc.). Cada elemento: **`{ id, name }`**. El **`id`** debe ser el **UUID del vault** (el mismo `vaultUuid` que usarán los fetchers).
 * 2. **`isLoadingDrives`** — `true` mientras cargás la lista de vaults; muestra el estado de carga en el cuerpo.
 * 3. **`fetchDriveRoot(vaultUuid)`** — Se llama al expandir un drive. Debe resolver a **`{ folders: Fila[], documents: Fila[] }`** con filas en el **mismo formato crudo** que devuelve tu backend (snake_case o camelCase). El hook entiende `folder_uuid` / `folderUuid`, `parent_folder_uuid`, `document_uuid` / `documentUuid`, `name`, `file_uuid`, etc. (véase `useDrivePickerTree`).
 * 4. **`fetchFolderChildren({ folderUuid, vaultUuid })`** — Igual al expandir una carpeta; mismas formas en `folders` y `documents`.
 * 5. **`onConfirm(items)`** — Recibe un arreglo de **`{ document_uuid, name, file_uuid?, file_id? }`** por cada documento seleccionado.
 *
 * Si preferís separar lógica, podés usar solo **`useDrivePickerTree`** con los mismos fetchers y montar otra UI.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(items: object[]) => void} [props.onConfirm]
 * @param {Array<{ id: string, name: string }>} props.drives — Raíz del árbol: un ítem por vault; `id` = vault UUID.
 * @param {boolean} [props.isLoadingDrives=false]
 * @param {(vaultUuid: string) => Promise<{ folders?: object[], documents?: object[] }>} props.fetchDriveRoot
 * @param {(args: { folderUuid: string, vaultUuid: string }) => Promise<{ folders?: object[], documents?: object[] }>} props.fetchFolderChildren
 * @param {string} [props.title]
 * @param {string} [props.cancelLabel]
 * @param {string} [props.addSelectedLabel] — Texto del botón principal sin contador; se añade ` (n)`.
 * @param {string} [props.processingLabel]
 * @param {string} [props.loadingLabel]
 * @param {string} [props.emptyDrivesMessage]
 * @param {string} [props.emptyDriveMessage]
 * @param {string} [props.emptyFolderMessage]
 * @param {string} [props.subtitle] — Subtítulo bajo el título (`ModalLayout`).
 * @param {string} [props.closeAriaLabel]
 * @param {string} [props.backdropClassName] — P.ej. `!inset-0` en Storybook para cubrir todo el viewport.
 * @param {string} [props.maxWidthClassName='max-w-[600px]'] — Ancho máximo del panel (por defecto 600px).
 * @param {string} [props.heightClassName='max-h-[600px] min-h-0'] — Tope de alto del panel; el árbol hace scroll dentro.
 * @param {string} [props.panelClassName] — Clases extra en el panel (`ModalLayout`).
 * @param {boolean} [props.useBodyPortal=false] — Si es true, `ModalLayout` monta en `document.body` (Storybook / ancestros con overflow o transform).
 * @param {string} [props.buttonText] — If set (non-empty after trim), renders an opener **`Button`** before the modal; use with **`onClick`** (e.g. `setOpen(true)`).
 * @param {(e: import('react').MouseEvent<HTMLButtonElement>) => void} [props.onClick] — Click handler for that opener button only (not the modal footer).
 */
const DrivePickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  drives = [],
  isLoadingDrives = false,
  fetchDriveRoot,
  fetchFolderChildren,
  title = 'AIMS Drive',
  subtitle,
  cancelLabel = 'Cancelar',
  addSelectedLabel = 'Añadir seleccionados',
  processingLabel = 'Procesando…',
  loadingLabel = 'Cargando…',
  emptyDrivesMessage = 'No hay drives disponibles',
  emptyDriveMessage = 'Drive vacío',
  emptyFolderMessage = 'Carpeta vacía',
  closeAriaLabel = 'Cerrar',
  backdropClassName = '',
  maxWidthClassName = 'max-w-[600px]',
  heightClassName = 'max-h-[600px] min-h-0',
  panelClassName = '',
  useBodyPortal = false,
  buttonText,
  onClick,
}) => {
  const titleId = useId();
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDismiss = useCallback(() => {
    if (isProcessing) return;
    onClose?.();
  }, [isProcessing, onClose]);

  const {
    treeData,
    loadingFolders,
    loadingDrives,
    loadDriveContent,
    loadFolderChildren,
    DRIVE_PREFIX,
    FOLDER_PREFIX,
  } = useDrivePickerTree({
    drives,
    isOpen,
    fetchDriveRoot,
    fetchFolderChildren,
  });

  useEffect(() => {
    if (!isOpen) {
      setExpandedFolders({});
      setSelectedFiles({});
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const handleToggle = useCallback(
    (file) => {
      const isDrive = file.type === 'drive';
      const isFolder = file.type === 'folder';
      const itemId = file.id;
      const folderUuid = file.folder_uuid || file.id;
      const vaultUuid = file.vault_uuid || file.id;

      if (isDrive) {
        const wasExpanded = !!expandedFolders[itemId];
        setExpandedFolders((prev) => {
          if (prev[itemId]) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [itemId]: true };
        });
        if (!wasExpanded) {
          loadDriveContent(itemId);
        }
      } else if (isFolder) {
        const wasExpanded = !!expandedFolders[itemId];
        setExpandedFolders((prev) => {
          if (prev[itemId]) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [itemId]: true };
        });
        if (!wasExpanded) {
          loadFolderChildren(folderUuid, vaultUuid);
        }
      } else {
        // Store the full node object so handleConfirm can read document_uuid
        // and name directly from it — eliminates the findDocumentInTree
        // re-lookup that could return a different row if two documents share a
        // normalized id or the tree has been partially refreshed.
        setSelectedFiles((prev) => {
          if (prev[itemId]) {
            const { [itemId]: removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [itemId]: file };
        });
      }
    },
    [expandedFolders, loadDriveContent, loadFolderChildren]
  );

  const driveFileItems = useMemo(
    () => (drives ?? []).map((d) => ({ ...d, type: 'drive' })),
    [drives],
  );

  const renderTree = useCallback(
    (parentKey, level = 0) => {
      if (level > 10) return null;

      const parentData = treeData[parentKey];
      if (!parentData) return null;

      const { folders = [], documents = [] } = parentData;
      const allItems = [...folders, ...documents];

      if (allItems.length === 0) return null;

      const nestedShell =
        level <= 2
          ? 'ml-4 border-l-2 border-edge pl-3'
          : 'ml-3 border-l-2 border-edge pl-2';

      return (
        <>
          {allItems.map((item) => {
            const isFolderItem = item.type === 'folder';
            const itemId = item.id;
            const folderUuid = isFolderItem
              ? item.folder_uuid || item.id
              : null;
            const folderKey = `${FOLDER_PREFIX}${folderUuid}`;

            const isExpanded = isFolderItem && !!expandedFolders[itemId];
            const isLoading =
              isFolderItem &&
              folderUuid &&
              !!loadingFolders[folderUuid];
            const isSelected = !isFolderItem && !!selectedFiles[itemId];
            const hasChildren =
              isFolderItem && folderUuid && treeData[folderKey];
            const childData = hasChildren ? treeData[folderKey] : null;
            const hasContent =
              childData &&
              (childData.folders?.length > 0 ||
                childData.documents?.length > 0);

            return (
              <div key={itemId} className="w-full">
                <FileItem
                  file={item}
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  isLoading={isLoading}
                  onToggle={handleToggle}
                />
                {isExpanded && isFolderItem && folderUuid ? (
                  <div className={`flex w-full flex-col ${nestedShell}`}>
                    {isLoading && !hasChildren && (
                      <TreeLoadingRow message={loadingLabel} />
                    )}
                    {hasChildren &&
                      hasContent &&
                      renderTree(folderKey, level + 1)}
                    {hasChildren && !hasContent && !isLoading && (
                      <div className="px-3 py-2 text-sm text-content-muted">
                        {emptyFolderMessage}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      );
    },
    [
      treeData,
      expandedFolders,
      loadingFolders,
      selectedFiles,
      handleToggle,
      loadingLabel,
      emptyFolderMessage,
    ]
  );

  const renderDriveLevel = useCallback(() => {
    if (!driveFileItems.length) return null;
    return driveFileItems.map((drive) => {
      const itemId = drive.id;
      const driveKey = `${DRIVE_PREFIX}${itemId}`;
      const isExpanded = !!expandedFolders[itemId];
      const isLoading = !!loadingDrives[itemId];
      const driveData = treeData[driveKey];
      const hasChildren = !!driveData;
      const hasContent =
        driveData &&
        (driveData.folders?.length > 0 || driveData.documents?.length > 0);

      return (
        <div key={itemId} className="w-full">
          <FileItem
            file={drive}
            isExpanded={isExpanded}
            isSelected={false}
            isLoading={isLoading}
            onToggle={handleToggle}
          />
          {isExpanded ? (
            <div className="ml-4 flex w-full flex-col border-l-2 border-edge pl-3">
              {isLoading && !hasChildren && (
                <TreeLoadingRow message={loadingLabel} />
              )}
              {hasChildren && hasContent && renderTree(driveKey, 1)}
              {hasChildren && !hasContent && !isLoading && (
                <div className="px-3 py-2 text-sm text-content-muted">
                  {emptyDriveMessage}
                </div>
              )}
            </div>
          ) : null}
        </div>
      );
    });
  }, [
    driveFileItems,
    expandedFolders,
    loadingDrives,
    treeData,
    DRIVE_PREFIX,
    handleToggle,
    renderTree,
    loadingLabel,
    emptyDriveMessage,
  ]);

  const selectedCount = useMemo(
    () => Object.keys(selectedFiles).length,
    [selectedFiles]
  );

  const handleConfirm = useCallback(async () => {
    if (selectedCount === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      // Build items directly from the stored node objects — no tree re-lookup,
      // so the confirmed document_uuid + name always match the clicked row.
      const items = buildConfirmItems(selectedFiles);
      onConfirm?.(items);
      onClose?.();
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedFiles,
    selectedCount,
    isProcessing,
    onConfirm,
    onClose,
  ]);

  const showLoading = isLoadingDrives;
  const openerLabel = buttonText == null ? '' : String(buttonText).trim();
  const showOpenerButton = openerLabel.length > 0;

  return (
    <>
      {showOpenerButton ? (
        <Button type="button" variant="primary" size="md" onClick={onClick}>
          {openerLabel}
        </Button>
      ) : null}
      <ModalLayout
        isOpen={isOpen}
        onClose={handleDismiss}
        title={title}
        subtitle={subtitle}
        titleId={titleId}
        closeAriaLabel={closeAriaLabel}
        backdropClassName={backdropClassName}
        maxWidthClassName={maxWidthClassName}
        heightClassName={heightClassName}
        panelClassName={panelClassName}
        useBodyPortal={useBodyPortal}
      >
        <div
          className={`${DEFAULT_BODY_SCROLLBAR_CLASS} flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-y-auto px-5 py-4 pr-3`}
        >
          {showLoading ? (
            <div className="flex min-h-[200px] flex-1 items-center justify-center py-8">
              <Loader2
                className="h-9 w-9 animate-spin text-brand-500"
                aria-label={loadingLabel}
              />
            </div>
          ) : drives?.length > 0 ? (
            <div className="flex w-full flex-col gap-0">{renderDriveLevel()}</div>
          ) : (
            <p className="py-5 text-center text-sm text-content-muted">
              {emptyDrivesMessage}
            </p>
          )}
        </div>

        <div className="w-full shrink-0 border-t border-edge bg-surface-muted/20 px-5 py-4">
          <div className="flex justify-end gap-4">
            <Button
              variant="tertiary"
              type="button"
              size="md"
              onClick={handleDismiss}
              disabled={isProcessing}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              type="button"
              size="md"
              onClick={handleConfirm}
              disabled={selectedCount === 0 || isProcessing}
              loading={isProcessing}
            >
              {isProcessing
                ? processingLabel
                : `${addSelectedLabel} (${selectedCount})`}
            </Button>
          </div>
        </div>
      </ModalLayout>
    </>
  );
};

export default DrivePickerModal;
